import { orderType, ReceiveOrderType } from "@/config";
import React, { useEffect, useRef, useState } from "react";
import { useAppContext } from "@/use-context/Context";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import NoOrder from "@/assets/images/bell (1) 1.png";
import ListOrderFooter from "./ListOrderFooter";
import socket from "@/utils/socket";
import { SocketEvent } from "@/enum/socketEvent";
import { useTranslation } from "react-i18next";
import OrderCard from "./OrderCard";
import { showToast } from "@/utils/toast";
import Notification from "@/components/Base/Notification";
import Lucide from "@/components/Base/Lucide";

interface tableIdProps {
    tableId: string;
    orderObj: orderType[];
    fetchOrderByTableId: () => void;
    isCate: boolean
}

export default function ListOrder({ tableId, orderObj, fetchOrderByTableId, isCate }: tableIdProps) {
    const { t } = useTranslation();
    const { setListItems, addToCart, setAddToCart, loading } = useAppContext();

    const scrollRef = useRef<HTMLDivElement | null>(null);

    const [selectAll, setSelectAll] = useState<boolean>(() => {
        const storedSelectAll = localStorage.getItem("selectAll");
        return storedSelectAll ? JSON.parse(storedSelectAll) : false;
    });
    const [updateOrder, setUpdateOrder] = useState<ReceiveOrderType[]>([]);

    useEffect(() => {
        localStorage.setItem("selectAll", JSON.stringify(selectAll));
    }, [selectAll]);

    const handleIncrease = (id: string, index: number, discountPercent: number, qtyStock: number, qty: number) => {
        if ((qty + 1) <= qtyStock) {
            if (discountPercent != null && discountPercent > 0) {
                setListItems(prevListItems =>
                    prevListItems.map(item =>
                        item._id === id && item.discountPercent === discountPercent ? { ...item, quantity: item.quantity + 1 } : item
                    )
                );

                setAddToCart(prevAddToCart =>
                    prevAddToCart.map(item =>
                        item._id === id && item.discountPercent === discountPercent ? { ...item, quantity: item.quantity + 1 } : item
                    )
                );
            } else {
                setListItems(prevListItems =>
                    prevListItems.map(item =>
                        item._id === id && !item.discountPercent ? { ...item, quantity: item.quantity + 1 } : item
                    )
                );

                setAddToCart(prevAddToCart =>
                    prevAddToCart.map(item =>
                        item._id === id && !item.discountPercent ? { ...item, quantity: item.quantity + 1 } : item
                    )
                );
            }
        } else {
            showToast("failed-add-stock");
        }
    }


    const handleDecrease = (id: string, index: number, discountPercent: number) => {
        const hasDiscount = discountPercent != null && discountPercent > 0;

        if (hasDiscount) {
            setListItems(prevListItems =>
                prevListItems.map(item =>
                    item._id === id && item.discountPercent === discountPercent ? { ...item, quantity: item.quantity - 1 } : item
                )
            );
        } else {
            setListItems(prevListItems =>
                prevListItems.map(item =>
                    item._id === id && !item.discountPercent ? { ...item, quantity: item.quantity - 1 } : item
                )
            );
        }

        setAddToCart(prevCart => {
            return prevCart
                .map(item => {
                    const isTargetItem = hasDiscount
                        ? (item._id === id && item.discountPercent === discountPercent)
                        : (item._id === id && !item.discountPercent);

                    if (isTargetItem) {
                        return { ...item, quantity: item.quantity - 1 };
                    }
                    return item;
                })
                .filter(item => item.quantity > 0);
        });
    }

    const handleSelectAll = () => {
        const newSelectAll = !selectAll;
        setSelectAll(newSelectAll);
        setAddToCart(prevData =>
            prevData.map(item =>
                item.status < 2 ? { ...item, isCheck: newSelectAll } : item
            )
        );
    };

    const handleCheck = (_id: string) => {
        setAddToCart(prevData =>
            prevData.map(item =>
                item._id === _id ? { ...item, isCheck: !item.isCheck } : item
            )
        );
    };

    const handleRemoveItem = (_id: string, index: number) => {
        setListItems(prevListItems =>
            prevListItems.map(item =>
                item._id === _id ? { ...item, quantity: 0 } : item
            )
        );

        setAddToCart(prev => prev.filter((_, i) => i !== index));
    };

    const handleNote = (event: React.ChangeEvent<HTMLInputElement>, _id: string) => {
        setAddToCart(prevData =>
            prevData.map(item =>
                item._id === _id ? { ...item, instruction: event.target.value } : item
            )
        );
    };

    const disableAll = addToCart && addToCart.filter(item => item.isWaiter || item.status === 1);

    useEffect(() => {
        const handleUpdate = (data: any) => {
            if (!data) return;
            if (data.success) {
                fetchOrderByTableId();
            }
        };

        const events = [
            SocketEvent.WAITER_RECEIVE_ORDER_FROM_CUSTOMER,
            SocketEvent.WAITER_RECEIVE_PAYMENT,
            SocketEvent.REJECT_ORDER,
            SocketEvent.ACCEPT_ORDER,
            SocketEvent.COOK_ORDER,
            SocketEvent.READy_ORDER,
            SocketEvent.DONE_ORDER,
            SocketEvent.FREE_ITEM_REQUEST,
        ];

        events.forEach((event) => {
            socket.on(event, handleUpdate);
        });

        return () => {
            events.forEach((event) => {
                socket.off(event, handleUpdate);
            });
        };
    }, []);

    useEffect(() => {
        fetchOrderByTableId()
    }, []);

    const functionUpdateOrder = (data: ReceiveOrderType[]) => {
        try {
            setAddToCart(prevData =>
                prevData.map(cartItem => {
                    let updatedItem = cartItem;

                    data.forEach(update => {
                        const items = Array.isArray(update?.data) ? update.data : (update?.data ? [update.data] : []);
                        items.forEach((dataItem: any) => {
                            if (dataItem.orderId === cartItem.orderId && dataItem.detailId === cartItem._id) {
                                updatedItem = { ...cartItem, status: update.status };
                            }
                        });
                    });

                    return updatedItem;
                })
            );
        } catch (error) {
            console.log(error, "error");

        } finally {
            setUpdateOrder([])
        }
    };

    useEffect(() => {
        if (updateOrder.length) {
            functionUpdateOrder(updateOrder)
        }
    }, [updateOrder]);

    const sortedItems = [...addToCart].sort((a, b) => {
        if (a.status !== b.status) {
            return a.status - b.status;
        }

        const timeA = new Date(a.tracking?.pending || 0).getTime();
        const timeB = new Date(b.tracking?.pending || 0).getTime();

        return timeA - timeB;
    });

    const prevLength = useRef(addToCart.length);

    useEffect(() => {
        if (addToCart.length > prevLength.current) {
            scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
        }
        prevLength.current = addToCart.length;
    }, [addToCart.length]);

    return (
        <>
            <div className="">
                <div className="flex justify-between items-center py-4 px-2">
                    <div className="flex items-center gap-1">
                        {
                            disableAll.length ?
                                <input
                                    className="mr-1 cursor-pointer w-5 h-5 rounded-[4px] text-primary border-[#C1C1C1] focus:outline-none focus:ring-0"
                                    type="checkbox"
                                    id="select-all"
                                    checked={selectAll}
                                    onChange={handleSelectAll}
                                />
                                :
                                <input
                                    className="mr-1 cursor-pointer w-5 h-5 rounded-[4px] text-primary border-[#C1C1C1]/30 focus:outline-none focus:ring-0"
                                    type="checkbox"
                                    id="select-all"
                                    checked={selectAll}
                                    disabled
                                    readOnly
                                />
                        }
                        <h1 className="text-btn text-[#202020]">{t('selectAll')}</h1>
                    </div>
                </div>
                <hr color="#ECE7E7" />
            </div>
            {
                (loading && !isCate) ?
                    <div className="h-[40vh] flex items-center justify-center">
                        <AiOutlineLoading3Quarters size={32} className="animate-spin inline-block text-[#767676]" />
                    </div>
                    :
                    addToCart.length ?
                        <div ref={scrollRef} className="overflow-auto max-h-[calc(100vh-230px)] md:pb-24 md:max-h-[calc(100vh-250px)]">
                            {addToCart.map((item, index) =>
                                item.isWaiter && (
                                    <OrderCard
                                        key={`waiter-${index}`}
                                        item={item}
                                        subIndex={index}
                                        handleIncrease={handleIncrease}
                                        handleDecrease={handleDecrease}
                                        handleCheck={handleCheck}
                                        handleRemoveItem={handleRemoveItem}
                                        handleNote={handleNote}
                                        fetchOrderByTableId={fetchOrderByTableId}
                                    />
                                )
                            )}

                            {sortedItems.map((item, index) =>
                                !item.isWaiter && (
                                    <OrderCard
                                        key={item._id}
                                        item={item}
                                        subIndex={index}
                                        handleIncrease={handleIncrease}
                                        handleDecrease={handleDecrease}
                                        handleCheck={handleCheck}
                                        handleRemoveItem={handleRemoveItem}
                                        handleNote={handleNote}
                                        fetchOrderByTableId={fetchOrderByTableId}
                                    />
                                )
                            )}
                        </div>
                        :
                        <div className="h-[40vh] flex items-center justify-center">
                            <div className="flex flex-col items-center">
                                <div className="min-h-[80] min-w-[80] max-w-[80]">
                                    <img
                                        src={NoOrder}
                                        className="rounded-[10px] object-cover h-[80] w-[80px]"
                                        alt="Asian Food"
                                    />
                                </div>
                                <p className="text-[16px] font-medium text-[#767676] ">{t('noOrder')}</p>
                            </div>
                        </div>
            }

            <ListOrderFooter
                orderObj={orderObj}
                tableId={tableId ? tableId : ""}
                fetchOrderByTableId={fetchOrderByTableId}
                resetSelectAll={() => setSelectAll(false)}
            />

            <Notification id="failed-add-stock" className="flex">
                <Lucide icon="XCircle" className="text-danger" />
                <div className="ml-4 mr-4">
                    <div className="font-medium">{t("outOfStock")}</div>
                </div>
            </Notification>
        </>
    );
}
